import { SbbOption } from "@sbb-esta/lyne-react/option";
import { SbbSelect } from "@sbb-esta/lyne-react/select";
import { useQuery } from "@tanstack/react-query";
import classNames from "classnames";
import { formatEventTime, getBoardRouteKey } from "helpers";
import { StandardMeetingModel } from "models/StandardMeetingModel";
import { useApi } from "providers";
import { useBoard } from "providers";
import { useTranslations } from "providers";
import React from "react";

const logger = createLogger("MeetingTemplatePicker");

import { createLogger } from "logging";

import { FormInput } from "./FormInput";

interface MeetingTemplatePickerProps extends FormInput {
  value: number | undefined;
  className?: string;
  showEmpty?: boolean;
  isDisabled: (item: StandardMeetingModel) => boolean;
  onChange: (id: number, time?: string) => void;
}

export const MeetingTemplatePicker: React.FC<MeetingTemplatePickerProps> = ({
  name,
  value,
  className,
  onChange,
  showEmpty = true,
  isDisabled = () => false,
}) => {
  const i18n = useTranslations();
  const { board } = useBoard();
  const { standardMeetingsApi } = useApi();

  const boardKey = getBoardRouteKey(board);
  const queryResult = useQuery({
    queryKey: [`Board|${boardKey}|MeetingTemplates`],
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      const data = await standardMeetingsApi.getAll(signal);
      logger.trace("Loaded", data);
      return data;
    },
    enabled: !!boardKey,
  });
  const isLoading = queryResult.isPending;
  const items = queryResult.data || [];

  const onSelect = (id: number) => {
    const item = items.find((x) => x.id === id);
    if (item) {
      const { startHours, startMinutes } = item;
      const time = formatEventTime(startHours, startMinutes);
      onChange(id, time);
    } else {
      onChange(id, undefined);
    }
  };

  return (
    <SbbSelect className={classNames(className)} name={name} style={{ width: "200px" }}>
      {isLoading && <SbbOption>{i18n.Loading_MeetingTemplates}</SbbOption>}
      {!isLoading && (
        <>
          {showEmpty && <SbbOption>{i18n.MultiSelect_NoneSelected}</SbbOption>}
          {items.map((option) => {
            const id = option.id;
            const optionValue = id.toString();
            const optionDisabled = isDisabled(option);
            const optionTime = formatEventTime(option.startHours, option.startMinutes);
            const key = `${name}-${option.title}`;

            return (
              <SbbOption
                key={key}
                value={optionValue}
                data-time={optionTime}
                disabled={optionDisabled}
                selected={value === id}
                onClick={() => onSelect(id)}
              >
                {option.title}
              </SbbOption>
            );
          })}
        </>
      )}
    </SbbSelect>
  );
};
